/**
 * ComparisonContext - IMPROVEMENT 11
 * Global context for stock comparison tray.
 */
import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'

export interface ComparisonStock {
  ticker: string
  name: string
  price: number
  change: number
  changePercent: number
  addedAt: number
}

interface ComparisonContextType {
  stocks: ComparisonStock[]
  addStock: (stock: Omit<ComparisonStock, 'addedAt'>) => void
  removeStock: (ticker: string) => void
  clearAll: () => void
  isInComparison: (ticker: string) => boolean
  maxStocks: number
  canAdd: boolean
}

const ComparisonContext = createContext<ComparisonContextType | null>(null)

const MAX_COMPARISON_STOCKS = 4
const STORAGE_KEY = 'apex_comparison_stocks'

export function ComparisonProvider({ children }: { children: ReactNode }) {
  const [stocks, setStocks] = useState<ComparisonStock[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stocks))
  }, [stocks])

  const addStock = useCallback((stock: Omit<ComparisonStock, 'addedAt'>) => {
    setStocks(prev => {
      if (prev.length >= MAX_COMPARISON_STOCKS) return prev
      if (prev.some(s => s.ticker === stock.ticker)) return prev

      return [...prev, { ...stock, addedAt: Date.now() }]
    })
  }, [])

  const removeStock = useCallback((ticker: string) => {
    setStocks(prev => prev.filter(s => s.ticker !== ticker))
  }, [])

  const clearAll = useCallback(() => {
    setStocks([])
  }, [])

  const isInComparison = useCallback((ticker: string) => {
    return stocks.some(s => s.ticker === ticker)
  }, [stocks])

  return (
    <ComparisonContext.Provider
      value={{
        stocks,
        addStock,
        removeStock,
        clearAll,
        isInComparison,
        maxStocks: MAX_COMPARISON_STOCKS,
        canAdd: stocks.length < MAX_COMPARISON_STOCKS
      }}
    >
      {children}
    </ComparisonContext.Provider>
  )
}

export function useComparison() {
  const context = useContext(ComparisonContext)
  if (!context) {
    throw new Error('useComparison must be used within a ComparisonProvider')
  }
  return context
}

export default ComparisonContext
